import { z } from 'zod'

import { isOneOf } from './helpers'

// -- Template form --

export const TEMPLATE_TYPES = [
  { value: 'tip', label: 'Tip' },
  { value: 'caseStudy', label: 'Case Study' },
  { value: 'story', label: 'Story' },
  { value: 'howTo', label: 'How-to' },
  { value: 'question', label: 'Question' },
  { value: 'behindTheScenes', label: 'Behind the Scenes' },
  { value: 'opinion', label: 'Opinion' },
  { value: 'list', label: 'List' },
] as const

export const TEMPLATE_GOALS = [
  { value: 'engagement', label: 'Engagement' },
  { value: 'connection', label: 'Connection' },
  { value: 'reflection', label: 'Reflection' },
  { value: 'expertise', label: 'Expertise' },
  { value: 'promotion', label: 'Promotion' },
  { value: 'education', label: 'Education' },
  { value: 'celebration', label: 'Celebration' },
] as const

export const TEMPLATE_STRUCTURES = [
  { value: 'singleText', label: 'Single Text' },
  { value: 'threaded', label: 'Threaded' },
] as const

export const TEMPLATE_VISIBILITY = [
  { value: 'private', label: 'Private' },
  { value: 'internal', label: 'Internal' },
  { value: 'public', label: 'Public' },
] as const

export const TEMPLATE_USER_TYPES = [
  { value: 'business', label: 'Business' },
  { value: 'individual', label: 'Individual' },
] as const

const TEMPLATE_TYPE_VALUES: [
  'tip',
  'caseStudy',
  'story',
  'howTo',
  'question',
  'behindTheScenes',
  'opinion',
  'list',
] = [
  'tip',
  'caseStudy',
  'story',
  'howTo',
  'question',
  'behindTheScenes',
  'opinion',
  'list',
]
const TEMPLATE_GOAL_VALUES: [
  'engagement',
  'connection',
  'reflection',
  'expertise',
  'promotion',
  'education',
  'celebration',
] = [
  'engagement',
  'connection',
  'reflection',
  'expertise',
  'promotion',
  'education',
  'celebration',
]
const TEMPLATE_STRUCTURE_VALUES: ['singleText', 'threaded'] = [
  'singleText',
  'threaded',
]
const TEMPLATE_VISIBILITY_VALUES: ['private', 'internal', 'public'] = [
  'private',
  'internal',
  'public',
]
const TEMPLATE_USER_TYPE_VALUES: ['business', 'individual'] = [
  'business',
  'individual',
]

export const templateFormSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200),
  body: z.string().min(1, 'Body is required').max(5000),
  description: z.string().min(1, 'Description is required').max(500),
  emoji: z.string().min(1),
  type: z.enum(TEMPLATE_TYPE_VALUES),
  goal: z.enum(TEMPLATE_GOAL_VALUES),
  structure: z.enum(TEMPLATE_STRUCTURE_VALUES),
  visibility: z.enum(TEMPLATE_VISIBILITY_VALUES),
  userTypes: z
    .array(z.enum(TEMPLATE_USER_TYPE_VALUES))
    .min(1, 'Select at least one user type'),
  featured: z.boolean(),
  tags: z.array(z.enum(['new', 'featured'])),
})

export type TemplateFormValues = z.infer<typeof templateFormSchema>

export const templateFormDefaults: TemplateFormValues = {
  title: '',
  body: '',
  description: '',
  emoji: '📝',
  type: 'tip',
  goal: 'engagement',
  structure: 'singleText',
  visibility: 'private',
  userTypes: ['business', 'individual'],
  featured: false,
  tags: [],
}

// -- Campaign form --

export const CAMPAIGN_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'ended', label: 'Ended' },
] as const

export const CAMPAIGN_TYPES = [
  { value: 'collection', label: 'Collection' },
  { value: 'dripCampaign', label: 'Drip Campaign' },
] as const

export const BANNER_COLORS = [
  { value: 'none', label: 'None' },
  { value: 'blue', label: 'Blue' },
  { value: 'green', label: 'Green' },
  { value: 'purple', label: 'Purple' },
  { value: 'red', label: 'Red' },
  { value: 'yellow', label: 'Yellow' },
] as const

const CAMPAIGN_STATUS_VALUES: ['draft', 'active', 'ended'] = [
  'draft',
  'active',
  'ended',
]
const CAMPAIGN_TYPE_VALUES: ['collection', 'dripCampaign'] = [
  'collection',
  'dripCampaign',
]
const BANNER_COLOR_VALUES: [
  'none',
  'blue',
  'green',
  'purple',
  'red',
  'yellow',
] = ['none', 'blue', 'green', 'purple', 'red', 'yellow']

const BANNER_COLOR_HEX_MAP: Record<string, string> = {
  blue: '#2196F3',
  green: '#4CAF50',
  purple: '#9C27B0',
  red: '#F44336',
  yellow: '#FFEB3B',
}

const HEX_TO_BANNER_COLOR = Object.fromEntries(
  Object.entries(BANNER_COLOR_HEX_MAP).map(([name, hex]) => [
    hex.toLowerCase(),
    name,
  ]),
)

/** Convert a banner color name to its hex code for the HexColorCode scalar. */
export function bannerColorToHex(color: string): string | null {
  return BANNER_COLOR_HEX_MAP[color] ?? null
}

type BannerColorValue = (typeof BANNER_COLORS)[number]['value']

/** Convert a hex code from the API back to a banner color name. */
export function hexToBannerColor(
  hex: string | null | undefined,
): BannerColorValue {
  if (!hex) return 'none'
  const match = HEX_TO_BANNER_COLOR[hex.toLowerCase()] ?? ''
  return isOneOf(match, BANNER_COLORS) ? match : 'none'
}

export const campaignFormSchema = z
  .object({
    slug: z.string().min(1, 'Slug is required'),
    name: z.string().min(1, 'Name is required'),
    title: z.string(),
    heading: z.string().min(1, 'Heading is required'),
    description: z.string(),
    status: z.enum(CAMPAIGN_STATUS_VALUES),
    campaignType: z.enum(CAMPAIGN_TYPE_VALUES),
    bannerColor: z.enum(BANNER_COLOR_VALUES),
    startDate: z.string().min(1, 'Start date is required'),
    endDate: z.string().min(1, 'End date is required'),
    showTemplatesAfterEndDate: z.boolean(),
    templateIds: z.array(z.string()),
  })
  .refine(
    (data) => {
      if (data.startDate && data.endDate) {
        return new Date(data.startDate) <= new Date(data.endDate)
      }
      return true
    },
    { message: 'End date must be after start date', path: ['endDate'] },
  )

export type CampaignFormValues = z.input<typeof campaignFormSchema>

export const campaignFormDefaults: CampaignFormValues = {
  slug: '',
  name: '',
  title: '',
  heading: '',
  description: '',
  status: 'draft',
  campaignType: 'collection',
  bannerColor: 'none',
  startDate: '',
  endDate: '',
  showTemplatesAfterEndDate: false,
  templateIds: [],
}
